import { FCElement } from '@fc/component';
import { FCEvents } from '@fc/events';

const CONTROL_INPUT_SELECTOR = 'input[name="properties[__shopify_send_gift_card_to_recipient]"]';

const FIELD_ERROR_MAP = {
  email: { inputRef: 'recipientEmail', errorRef: 'emailError' },
  name: { inputRef: 'recipientName', errorRef: 'nameError' },
  message: { inputRef: 'recipientMessage', errorRef: 'messageError' },
  send_on: { inputRef: 'recipientSendOn', errorRef: 'sendOnError' },
};

const ERROR_REFS = ['emailError', 'nameError', 'messageError', 'sendOnError'];

const pad = (n) => String(n).padStart(2, '0');
const toDateStr = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

class GiftCardRecipientForm extends FCElement {
  static DeliveryMode = {
    SELF: 'self',
    RECIPIENT: 'recipient_form',
  };

  #mode = GiftCardRecipientForm.DeliveryMode.SELF;
  #ac = null;

  requiredRefs = [
    'myEmailButton',
    'recipientEmailButton',
    'recipientFields',
    'recipientEmail',
    'recipientName',
    'recipientMessage',
    'recipientSendOn',
  ];

  get #fields() {
    const { recipientEmail, recipientName, recipientMessage, recipientSendOn } = this.refs;
    return [recipientEmail, recipientName, recipientMessage, recipientSendOn];
  }

  get #isSendingToRecipient() {
    return this.#mode === GiftCardRecipientForm.DeliveryMode.RECIPIENT;
  }

  get #idSuffix() {
    return this.dataset.blockId ?? this.dataset.sectionId ?? 'default';
  }

  connectedCallback() {
    super.connectedCallback();

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal, passive: true };

    this.#syncButtons(GiftCardRecipientForm.DeliveryMode.SELF);
    this.refs.recipientFields.hidden = true;
    this.refs.recipientFields.setAttribute('aria-hidden', 'true');
    this.#resetFields();
    this.#deactivateFields();
    this.#applyDateBounds();

    this.refs.recipientMessage.addEventListener('input', () => this.#refreshCharCount(), opts);
    document.addEventListener(FCEvents.cartFail, this.#onCartError, opts);
    document.addEventListener(FCEvents.cartRefresh, this.#onCartUpdate, opts);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#ac?.abort();
    this.#ac = null;
  }

  toggleRecipientForm(mode, _event) {
    if (!Object.values(GiftCardRecipientForm.DeliveryMode).includes(mode)) {
      throw new Error(
        `Invalid delivery mode: ${mode}. Must be one of: ${Object.values(GiftCardRecipientForm.DeliveryMode).join(
          ', ',
        )}`,
      );
    }
    if (this.#mode === mode) return;

    this.#mode = mode;
    this.#applyModeState();
  }

  #applyModeState() {
    const showRecipient = this.#isSendingToRecipient;

    this.#syncButtons(this.#mode);
    this.refs.recipientFields.hidden = !showRecipient;
    this.refs.recipientFields.setAttribute('aria-hidden', String(!showRecipient));

    if (showRecipient) {
      this.#activateFields();
      this.#refreshCharCount();
      this.#announce(FitCheck.translations?.recipient_form_fields_visible ?? '');
      this.refs.recipientEmail.focus();
    } else {
      this.#resetFields();
      this.#deactivateFields();
      this.#announce(FitCheck.translations?.recipient_form_fields_hidden ?? '');
    }

    this.dispatchEvent(
      new CustomEvent('recipient:toggle', {
        detail: { mode: this.#mode, recipientFormVisible: showRecipient },
        bubbles: true,
      }),
    );
  }

  #syncButtons(mode) {
    const isSelf = mode !== GiftCardRecipientForm.DeliveryMode.RECIPIENT;
    this.refs.myEmailButton.checked = isSelf;
    this.refs.recipientEmailButton.checked = !isSelf;
  }

  #resetFields() {
    for (const f of this.#fields) f && (f.value = '');
    this.#refreshCharCount();
    this.#wipeErrors();
  }

  #setFieldDescriptions(includeErrors = false) {
    for (const [field, { inputRef }] of Object.entries(FIELD_ERROR_MAP)) {
      const input = this.refs[inputRef];
      if (!(input instanceof HTMLElement)) continue;

      const describedBy = [];
      if (field === 'message') {
        describedBy.push(`Recipient-message-help-${this.#idSuffix}`);
      }
      if (includeErrors && input.getAttribute('aria-invalid') === 'true') {
        describedBy.push(`RecipientForm-${field}-error-${this.#idSuffix}`);
      }

      if (describedBy.length > 0) {
        input.setAttribute('aria-describedby', describedBy.join(' '));
      } else {
        input.removeAttribute('aria-describedby');
      }
    }
  }

  #deactivateFields() {
    for (const f of this.#fields) {
      if (!f) continue;
      f.disabled = true;
      f.removeAttribute('required');
      f.removeAttribute('aria-invalid');
    }

    this.querySelector(CONTROL_INPUT_SELECTOR)?.remove();

    if (this.refs.timezoneOffset) {
      this.refs.timezoneOffset.disabled = true;
      this.refs.timezoneOffset.value = '';
    }

    this.#wipeErrors();
  }

  #activateFields() {
    for (const f of this.#fields) {
      if (!f) continue;
      f.disabled = false;
      if (f === this.refs.recipientEmail) f.setAttribute('required', 'required');
    }

    if (!this.querySelector(CONTROL_INPUT_SELECTOR)) {
      const hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = 'properties[__shopify_send_gift_card_to_recipient]';
      hidden.value = 'on';
      this.appendChild(hidden);
    }

    if (this.refs.timezoneOffset) {
      this.refs.timezoneOffset.disabled = false;
      this.refs.timezoneOffset.value = new Date().getTimezoneOffset().toString();
    }

    this.#applyDateBounds();
    this.#setFieldDescriptions();
  }

  #refreshCharCount() {
    const el = this.refs.characterCount;
    const tpl = el?.getAttribute('data-template');
    if (!tpl) return;

    const { value, maxLength } = this.refs.recipientMessage;
    el.textContent = tpl.replace('[current]', String(value.length)).replace('[max]', String(maxLength));
  }

  #applyDateBounds() {
    const now = new Date();
    const end = new Date(now);
    end.setDate(now.getDate() + 90);
    this.refs.recipientSendOn.setAttribute('min', toDateStr(now));
    this.refs.recipientSendOn.setAttribute('max', toDateStr(end));
  }

  #onCartError = (event) => {
    const data = event.detail?.data;
    if (!data) return;

    const { message, errors, description } = data;

    if (errors && typeof errors === 'object') {
      this.#showErrors(message || window.FitCheck?.translations?.recipient_form_error || '', errors);
    } else if (message) {
      this.#showErrors(message, description);
    }
  };

  #showErrors(title, body) {
    this.#wipeErrors();

    if (body && typeof body === 'object') {
      for (const [field, msgs] of Object.entries(body)) {
        const mapping = FIELD_ERROR_MAP[field];
        if (!mapping) continue;

        const container = this.refs[mapping.errorRef];
        const input = this.refs[mapping.inputRef];

        const span = container?.querySelector('span');
        if (span) {
          span.textContent = `${Array.isArray(msgs) ? msgs.join(', ') : msgs}.`;
          container.classList.remove('hidden');
        }

        if (input instanceof HTMLElement) {
          input.setAttribute('aria-invalid', 'true');
        }
      }

      this.#setFieldDescriptions(true);
    }

    this.#announce(
      title ?? FitCheck.translations?.recipient_form_error ?? '',
    );
  }

  #wipeErrors() {
    for (const key of ERROR_REFS) {
      const box = this.refs[key];
      if (!(box instanceof HTMLElement)) continue;
      box.classList.add('hidden');
      const span = box.querySelector('span');
      if (span) span.textContent = '';
    }

    for (const f of this.#fields) {
      f?.removeAttribute('aria-invalid');
    }

    this.#setFieldDescriptions();

    if (this.refs.liveRegion) this.refs.liveRegion.textContent = '';
  }

  #announce(msg) {
    if (this.refs.liveRegion) this.refs.liveRegion.textContent = msg;
  }

  #onCartUpdate = () => this.#wipeErrors();
}

if (!customElements.get('gift-recipient')) {
  customElements.define('gift-recipient', GiftCardRecipientForm);
}
